'use client';

import { produce, Draft } from '@/globals/helpers/immer';
import React, { useCallback } from 'react';
import * as v from 'valibot';
import { ContactFormWebApiData } from './ContactForm.types';
import { getValidationSchema } from './helpers/useValidationSchema';
import {
	createSessionStorageStore,
	useWebStorageStore,
} from '@/globals/webStorage/useWebStorage';
import { usePathname } from 'next/navigation';

const AllContactFormsSchema = v.record(
	v.string(),
	v.object({
		isDirty: v.boolean(),
		previewMode: v.boolean(),
		formIssues: v.record(v.string(), v.string()),
		showServerError: v.boolean(),
		fields: v.record(
			v.string(),
			v.object({
				value: v.string(),
				isTouched: v.boolean(),
				isDirty: v.boolean(),
				isValid: v.boolean(),
				issue: v.optional(v.string()),
			}),
		),
	}),
);

const store = createSessionStorageStore({
	key: 'contactForms',
	schema: AllContactFormsSchema,
});

type AllContactFormsState = v.InferInput<typeof AllContactFormsSchema>;
type SingleContactFormState = AllContactFormsState[keyof AllContactFormsState];
type FieldState =
	SingleContactFormState['fields'][keyof SingleContactFormState['fields']];

const emptyForm: SingleContactFormState = {
	isDirty: false,
	previewMode: false,
	formIssues: {},
	fields: {},
	showServerError: false,
};

export const emptyField: FieldState = {
	value: '',
	isTouched: false,
	isDirty: false,
	isValid: true,
};

const initialContactFormsState: AllContactFormsState = {};

type Action =
	| ({
			type: 'previewContactForm';
	  } & {
			fieldsets: ContactFormWebApiData['form']['fieldsets'];
	  })
	| ({
			type: 'changeContactFormField';
	  } & {
			fieldName: string;
			fieldValue: string;
			schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>;
	  })
	| ({
			type: 'validateContactFormField';
	  } & {
			fieldName: string;
			schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>;
	  })
	| {
			type: 'escapePreviewMode';
	  }
	| {
			type: 'resetContactForm';
	  }
	| {
			type: 'showServerError';
	  };

const contactFormStateReducer = produce(
	// eslint-disable-next-line complexity
	(draftState: Draft<SingleContactFormState>, action: Action) => {
		switch (action.type) {
			case 'previewContactForm': {
				draftState.formIssues = {}; // reset any previous issues

				for (const { fields } of action.fieldsets) {
					for (const fieldData of fields) {
						const { name } = fieldData;

						// We need to validate each field, since it may not have been blurred/changed before
						const field = { ...(draftState!.fields[name] || emptyField) };
						const schema = getValidationSchema(fieldData);
						const parsed = v.safeParse(
							schema,
							draftState.fields[name]?.value ?? '',
						);
						const issue = parsed.issues?.[0].message;

						field.isDirty = true;
						field.isTouched = true;
						field.isValid = parsed.success;
						field.issue = issue;
						draftState.fields = { ...draftState.fields, [name]: field };

						if (issue != null) {
							draftState.formIssues = {
								...draftState.formIssues,
								[name]: issue,
							};
						}
					}
				}
				draftState.isDirty = true;
				// Only switch on preview-mode if we have 0 issues
				if (Object.keys(draftState.formIssues).length === 0)
					draftState.previewMode = true;

				return;
			}
			case 'escapePreviewMode': {
				draftState.showServerError = false; // remove any server errors; user is trying again
				draftState.previewMode = false;
				return;
			}
			case 'changeContactFormField': {
				const { fieldName, fieldValue, schema } = action;

				const field = { ...(draftState!.fields[fieldName] || emptyField) };

				field.value = fieldValue;
				field.isDirty = true;

				const parsed = v.safeParse(schema, field.value);
				field.isValid = parsed.success;
				field.issue = parsed.issues?.[0]?.message;

				draftState.fields = { ...draftState.fields, [fieldName]: field };
				return;
			}
			case 'validateContactFormField': {
				const { fieldName, schema } = action;

				const field = { ...(draftState!.fields[fieldName] || emptyField) };
				field.isTouched = true;

				const parsed = v.safeParse(schema, field.value);
				field.isValid = parsed.success;
				field.issue = parsed.issues?.[0]?.message;

				draftState.fields = { ...draftState.fields, [fieldName]: field };
				return;
			}
			case 'resetContactForm': {
				return emptyForm;
			}
			case 'showServerError': {
				// This looks like double state, since useFormState already has a 'rejected' state if there was a server error
				// However, we want to show/hide this error when the user switches back to form-mode from preview-mode, which needs this state
				draftState.showServerError = true;
				return;
			}
			default:
				return draftState;
		}
	},
);

const ContactFormContext = React.createContext<
	[SingleContactFormState, React.Dispatch<Action>] | undefined
>(undefined);

export const ContactFormProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const pathname = usePathname();
	const [allContactFormsState, setAllContactFormsState] = useWebStorageStore(
		store,
		initialContactFormsState,
	);
	const form: SingleContactFormState =
		allContactFormsState?.[pathname] != null
			? allContactFormsState[pathname]
			: emptyForm;

	const contactFormDispatch = useCallback(
		(action: Action) => {
			setAllContactFormsState((oldState) => ({
				...oldState,
				[pathname]: contactFormStateReducer(form, action),
			}));
		},
		[setAllContactFormsState, form, pathname],
	);

	return (
		<ContactFormContext.Provider value={[form, contactFormDispatch]}>
			{children}
		</ContactFormContext.Provider>
	);
};

export const useContactForm = () => {
	const context = React.useContext(ContactFormContext);

	if (context == null) {
		throw new Error(
			'useContactFormContext must be within ContactFormContextProvider',
		);
	}

	return context;
};
